import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Building,
  MapPin,
  Image,
  Layers,
  Check,
  ChevronRight,
  Lock,
  ExternalLink,
  Hash,
  Search,
  X,
  AlertCircle,
} from "lucide-react";
import { Map, AdvancedMarker, useMap, useMapsLibrary, ControlPosition } from "@vis.gl/react-google-maps";
import { Constants } from "../../../utils/constants";
import GooglePlacesSearchInput from "../host-add-space/components/GooglePlacesSearchInput";
import { motion, AnimatePresence } from "framer-motion";
import MapErrorBoundary from "../../../components/MapErrorBoundary";
import { buildingAddressService } from "../../../services/buildingAddressService";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import { showApiMessage } from "../../../utils/messageHandler";
import ManageFloorsDialog from "../host-add-space/components/ManageFloorsDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../shadcn/ui/dialog";
import { Button } from "../../../shadcn/ui/button";
import { Input } from "../../../shadcn/ui/input";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import toastUtils from "../../../utils/toast";
import HUpgradeButton from "../../../hcomponents/HUpgradeButton";
import { getUserTimezone } from "../../../utils/timezoneUtils";
import TimezoneSelect from "../../../components/TimezoneSelect";
import * as localService from "../../../services/localService";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import logger from "../../../utils/logger";

const STEPS = [
  { label: "Location", icon: MapPin },
  { label: "Details", icon: Building },
  { label: "Floors", icon: Layers },
];

const BuildingAddressSettings = () => {
  const history = useHistory();
  const featureFlags = useSelector(state => state.subscription?.featureFlags || {});

  const currentUser = localService.getUser();
  const orgType = currentUser?.organization?.type?.code || currentUser?.organizationType?.code;
  const isPropertyDistribution = orgType === "HOST_PROPERTY_DISTRIBUTION";
  const canManageMultipleLocations = isPropertyDistribution || !!featureFlags?.MULTI_LOCATION;

  // Get current user's organization ID for edit permission check
  const currentUserOrgId = currentUser?.organizationId || currentUser?.organization?.id;

  // Check if a building address can be edited by the current user
  // Returns true if organizationId is null (legacy/shared) or matches user's org
  const canEditAddress = address => {
    // If organizationId is null or undefined, allow editing (legacy or shared addresses)
    if (address.organizationId === null || address.organizationId === undefined) {
      return true;
    }
    // If organizationId matches user's org, allow editing
    return address.organizationId === currentUserOrgId;
  };

  // Check if a building address's floors can be managed by the current user
  // Floors can be managed by ANYONE, regardless of who created the building
  // This allows hosts to add floors to buildings created by other hosts
  const canManageFloors = _address => {
    // Floor management is available to all users for any building address
    return true;
  };

  const [buildingAddresses, setBuildingAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [mapError, setMapError] = useState(false);
  const pageSize = 10;

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [panTarget, setPanTarget] = useState(null);
  const geocodingLib = useMapsLibrary("geocoding");

  /**
   * Internal component to control the map's camera programmatically
   * without creating a "controlled component" lock.
   */
  const MapController = ({ target }) => {
    const map = useMap();
    useEffect(() => {
      if (!map || !target) return;
      map.panTo({ lat: Number(target.latitude), lng: Number(target.longitude) });
      const currentZoom = map.getZoom() || 0;
      const targetZoom = target.zoom || 17;
      if (currentZoom < targetZoom) {
        map.setZoom(targetZoom);
      }
    }, [map, target]);
    return null;
  };

  const [formData, setFormData] = useState({
    buildingName: "",
    streetAddress: "",
    city: "",
    region: "",
    country: "",
    postalCode: "",
    digitalAddress: "",
    latitude: -1.2921,
    longitude: 36.8219,
    locationAddress: "",
    images: [],
    timezone: getUserTimezone(),
  });

  useEffect(() => {
    fetchBuildingAddresses(currentPage);
  }, [currentPage]);

  const goToUpgrade = useCallback(() => {
    history.push("/host/pricing?view=packages");
  }, [history]);

  const fetchBuildingAddresses = async (page = 0) => {
    try {
      setLoading(true);
      const response = await buildingAddressService.getBuildingAddressesWithPagination(page, pageSize);
      if (response.status === 200) {
        const data = response.data;
        setBuildingAddresses(data.content || data.data || []);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
      } else {
        showApiMessage(response, "fetch building addresses");
      }
    } catch (error) {
      logger.error("Error fetching building addresses:", error);
      showApiMessage(error, "fetch building addresses");
    } finally {
      setLoading(false);
    }
  };

  // Search building addresses
  const handleSearch = async (query = searchQuery) => {
    if (!query || query.trim() === "") {
      // Clear search and fetch normal list
      setIsSearchMode(false);
      setSearchQuery("");
      setCurrentPage(0);
      fetchBuildingAddresses(0);
      return;
    }

    try {
      setIsSearching(true);
      setLoading(true);
      const response = await buildingAddressService.searchBuildingAddresses({
        buildingName: query,
        streetAddressContains: query,
        city: query,
        pageNumber: 0,
        pageSize: pageSize,
      });

      if (response.status === 200) {
        const data = response.data;
        setBuildingAddresses(data.content || data.data || []);
        setTotalPages(data.totalPages || 1);
        setTotalElements(data.totalElements || data.content?.length || 0);
        setIsSearchMode(true);
      } else {
        showApiMessage(response, "search building addresses");
      }
    } catch (error) {
      logger.error("Error searching building addresses:", error);
      showApiMessage(error, "search building addresses");
    } finally {
      setIsSearching(false);
      setLoading(false);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    setIsSearchMode(false);
    setCurrentPage(0);
    fetchBuildingAddresses(0);
  };

  // Handle search input key press - currently unused but kept for future use
  // eslint-disable-next-line no-unused-vars
  const _handleSearchKeyPress = e => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleImageUpload = async files => {
    try {
      setImageUploading(true);
      const validFiles = Array.from(files).filter(
        file => file.type.startsWith("image/") && file.size <= 5 * 1024 * 1024
      );
      if (validFiles.length === 0) {
        showApiMessage(
          { response: { status: 400, data: { message: "Please select valid image files (max 5MB each)" } } },
          "upload images"
        );
        return;
      }
      const uploadResponse = await spaceManagementService.uploadMedia(validFiles);
      if (uploadResponse.status === 200 || uploadResponse.status === 201) {
        setFormData(prev => ({ ...prev, images: [...prev.images, ...uploadResponse.data] }));
        showApiMessage(uploadResponse, "upload image(s)");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      logger.error("Error uploading images:", error);
      showApiMessage(error, "upload image(s)");
    } finally {
      setImageUploading(false);
    }
  };

  const removeImage = index => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleCreateAddress = async () => {
    if (!canManageMultipleLocations && buildingAddresses.length >= 1) {
      toastUtils.info("Multiple locations are available on higher plans.", {
        action: { label: "View plans", onClick: goToUpgrade },
      });
      return;
    }
    setIsSaving(true);
    try {
      const { locationAddress, ...addressData } = formData;
      const payload = {
        ...addressData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        timezone: formData.timezone || getUserTimezone(),
      };
      const response = await buildingAddressService.createBuildingAddress(payload);
      if (response.status === 200 || response.status === 201) {
        showApiMessage(response, "create building address");
        fetchBuildingAddresses(0);
        const createdAddress = response.data?.data || response.data;
        if (createdAddress?.id) {
          setEditingAddress(createdAddress);
          setCurrentStep(2); // advance straight to Floors
        } else {
          closeDialog();
        }
      } else {
        showApiMessage(response, "create building address");
      }
    } catch (error) {
      logger.error("Error creating building address:", error);
      showApiMessage(error, "create building address");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateAddress = async () => {
    if (!editingAddress) return;
    setIsSaving(true);
    try {
      const { locationAddress, ...addressData } = formData;
      const payload = {
        ...addressData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        timezone: formData.timezone || getUserTimezone(),
      };
      const response = await buildingAddressService.updateBuildingAddress(editingAddress.id, payload);
      if (response.status === 200) {
        showApiMessage(response, "update building address");
        fetchBuildingAddresses(0);
        setCurrentStep(2); // advance to Floors after saving
      } else {
        showApiMessage(response, "update building address");
      }
    } catch (error) {
      logger.error("Error updating building address:", error);
      showApiMessage(error, "update building address");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAddress = address => {
    // Guard: Prevent deleting if address belongs to another organization
    if (!canEditAddress(address)) {
      toastUtils.error("You don't have permission to delete this building address.");
      return;
    }
    setAddressToDelete(address.id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) return;
    try {
      const response = await buildingAddressService.deleteBuildingAddress(addressToDelete);
      if (response.status === 200 || response.status === 204) {
        showApiMessage(response, "delete building address");
        if (isSearchMode) {
          handleSearch(searchQuery);
        } else {
          fetchBuildingAddresses(currentPage);
        }
      } else {
        showApiMessage(response, "delete building address");
      }
    } catch (error) {
      logger.error("Error deleting building address:", error);
      showApiMessage(error, "delete building address");
    } finally {
      setShowDeleteConfirm(false);
      setAddressToDelete(null);
    }
  };

  const startEdit = address => {
    // Guard: Prevent editing if address belongs to another organization
    if (!canEditAddress(address)) {
      toastUtils.error("You don't have permission to edit this building address.");
      return;
    }

    setFormData({
      buildingName: address.buildingName,
      streetAddress: address.streetAddress,
      city: address.city,
      region: address.region,
      country: address.country,
      postalCode: address.postalCode,
      digitalAddress: address.digitalAddress || "",
      latitude: address.latitude,
      longitude: address.longitude,
      locationAddress: "",
      images: address.images || [],
      timezone: address.timezone || getUserTimezone(),
    });
    setEditingAddress(address);
    setPlacePicked(true);
    setCurrentStep(0);
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      buildingName: "",
      streetAddress: "",
      city: "",
      region: "",
      country: "",
      postalCode: "",
      digitalAddress: "",
      latitude: -1.2921,
      longitude: 36.8219,
      locationAddress: "",
      images: [],
      timezone: getUserTimezone(),
    });
    setEditingAddress(null);
    setPlacePicked(false);
    setCurrentStep(0);
  };

  const closeDialog = () => {
    setShowCreateForm(false);
    setMapError(false);
    resetForm();
  };

  const handleMapError = () => {
    setMapError(true);
  };

  const handleMapClick = event => {
    const lat = event.detail.latLng?.lat;
    const lng = event.detail.latLng?.lng;
    if (lat != null && lng != null) {
      setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
      setPanTarget({ latitude: lat, longitude: lng, zoom: 17, timestamp: Date.now() });
      setPlacePicked(true);

      // Try reverse geocoding to fill address details
      if (geocodingLib) {
        const geocoder = new geocodingLib.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results[0]) {
            const place = results[0];
            const components = {};
            (place.address_components || []).forEach(c => {
              (c.types || []).forEach(t => {
                if (!components[t]) components[t] = c.long_name;
              });
            });

            const countryComp = (place.address_components || []).find(
              c => Array.isArray(c.types) && c.types.includes("country")
            );

            setFormData(prev => ({
              ...prev,
              streetAddress: place.formatted_address?.split(",")[0] || prev.streetAddress,
              city: components.locality || components.administrative_area_level_2 || prev.city,
              region: components.administrative_area_level_1 || prev.region,
              country: components.country || countryComp?.long_name || prev.country,
              postalCode: components.postal_code || "00000",
            }));
          }
        });
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Google Maps place handler
  const handlePlaceSelected = parsed => {
    setFormData(prev => ({
      ...prev,
      buildingName: parsed.buildingName || prev.buildingName,
      streetAddress: parsed.streetAddress || prev.streetAddress,
      city: parsed.city || prev.city,
      region: parsed.region || prev.region,
      country: parsed.country || prev.country,
      postalCode: parsed.postalCode || prev.postalCode,
      digitalAddress: parsed.digitalAddress || parsed.postalCode || prev.digitalAddress,
      timezone: parsed.timezone || prev.timezone,
      latitude: parsed.latitude ?? prev.latitude,
      longitude: parsed.longitude ?? prev.longitude,
    }));
    setPlacePicked(true);
    setPanTarget({ latitude: parsed.latitude, longitude: parsed.longitude });
  };

  // Prevent dialog from closing when clicking on Google Places autocomplete suggestions
  const handlePointerDownOutside = event => {
    const target = event.target;
    if (
      target?.closest?.(".pac-container") ||
      target?.closest?.(".pac-item") ||
      target?.classList?.contains("pac-container") ||
      target?.classList?.contains("pac-item") ||
      target?.closest?.("[data-pac-container]")
    ) {
      event.preventDefault();
    }
  };

  const handleInteractOutside = event => {
    const target = event.target;
    if (
      target?.closest?.(".pac-container") ||
      target?.closest?.(".pac-item") ||
      target?.classList?.contains("pac-container") ||
      target?.classList?.contains("pac-item") ||
      target?.closest?.("[data-pac-container]")
    ) {
      event.preventDefault();
    }
  };

  const handleFocusOutside = event => {
    const target = event.target;
    if (
      target?.closest?.(".pac-container") ||
      target?.closest?.(".pac-item") ||
      target?.classList?.contains("pac-container") ||
      target?.classList?.contains("pac-item") ||
      target?.closest?.("[data-pac-container]")
    ) {
      event.preventDefault();
    }
  };

  const [placePicked, setPlacePicked] = useState(false);

  // Marker component for the map - now just a simple pin (AdvancedMarker handles position)
  const MapMarkerPin = () => (
    <div style={{ transform: "translate(-50%, -100%)", cursor: "pointer" }}>
      <MapPin className="w-8 h-8 text-red-500" fill="currentColor" stroke="white" strokeWidth={1} />
    </div>
  );

  const floorsUnlocked = !!editingAddress?.id;
  const multiLocationLimitReached = !canManageMultipleLocations && buildingAddresses.length >= 1;

  const handleOpenCreateForm = () => {
    resetForm();
    setShowCreateForm(true);
  };

  const getStepState = idx => {
    if (idx < currentStep) return "done";
    if (idx === currentStep) return "active";
    if (idx === 2 && !floorsUnlocked) return "locked";
    return "upcoming";
  };

  const stepCircleClass = idx => {
    const s = getStepState(idx);
    if (s === "done") return "bg-green-500 border-green-500 text-white";
    if (s === "active") return "bg-blue-600 border-blue-600 text-white ring-4 ring-blue-100";
    if (s === "locked") return "bg-white border-gray-200 text-gray-300 cursor-not-allowed";
    return "bg-white border-gray-300 text-gray-400 hover:border-blue-400 cursor-pointer";
  };

  const stepLabelClass = idx => {
    const s = getStepState(idx);
    if (s === "done") return "text-green-600";
    if (s === "active") return "text-blue-600 font-semibold";
    if (s === "locked") return "text-gray-300";
    return "text-gray-400";
  };

  const connectorClass = idx => (currentStep > idx ? "bg-blue-400" : "bg-gray-200");

  return (
    <>
      <h2 className="mx-3 mb-4 text-xl font-semibold text-gray-800">Building Addresses</h2>
      <div className="overflow-y-auto p-6 mx-3 mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm scrollbar-hide">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 mb-6 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Building Addresses</h3>
            <p className="text-sm text-gray-500 mt-0.5">Manage your physical locations, addresses, and floor plans.</p>
          </div>
          <button
            onClick={handleOpenCreateForm}
            className={`flex items-center px-4 py-2 space-x-2 text-white rounded-md transition ${
              multiLocationLimitReached ? "bg-slate-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
            }`}
            disabled={multiLocationLimitReached}
          >
            <Plus size={16} />
            <span>Add New Address</span>
          </button>
        </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
            <Search className="w-5 h-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search building addresses..."
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              // Debounce search
              clearTimeout(window.buildingSearchTimeout);
              window.buildingSearchTimeout = setTimeout(() => {
                handleSearch(e.target.value);
              }, 300);
            }}
            className="block py-2 pr-12 pl-10 w-full leading-5 placeholder-gray-500 bg-white rounded-md border border-gray-300 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Search Results Indicator */}
      {isSearchMode && (
        <div className="mb-4 flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Search className="w-4 h-4" />
            <span>
              Showing results for "<strong>{searchQuery}</strong>" — {totalElements} result
              {totalElements !== 1 ? "s" : ""} found
            </span>
          </div>
          <button onClick={handleClearSearch} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            Clear search
          </button>
        </div>
      )}

      {multiLocationLimitReached && FeatureAccess.canUpgradePlan(currentUser) && (
        <div className="mb-6 border border-slate-200 bg-slate-50 text-slate-800 px-4 py-3 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm">
            Your plan supports a single location. Upgrade to manage multiple building addresses.
          </p>
          {!isPropertyDistribution && <HUpgradeButton size="sm" onClick={goToUpgrade} />}
        </div>
      )}

      {/* ── Create / Edit Wizard Dialog ── */}
      <Dialog
        open={showCreateForm}
        onOpenChange={open => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent
          className="sm:max-w-[680px] max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden rounded-xl"
          onPointerDownOutside={handlePointerDownOutside}
          onInteractOutside={handleInteractOutside}
          onFocusOutside={handleFocusOutside}
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-gray-100">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {editingAddress ? "Edit Building Address" : "Add Building Address"}
            </DialogTitle>
            <DialogDescription className="mt-0.5 text-sm text-gray-500">
              {editingAddress
                ? "Update your building's details, location and floors."
                : "Fill in the details — you can add floors right after saving."}
            </DialogDescription>
          </div>

          {/* Step Indicator */}
          <div className="px-8 py-5 bg-gray-50/60 border-b border-gray-100">
            <div className="flex items-center">
              {STEPS.map((step, idx) => {
                const state = getStepState(idx);
                const canClick = state !== "locked" && state !== "active";
                return (
                  <React.Fragment key={idx}>
                    <button
                      type="button"
                      onClick={() => canClick && setCurrentStep(idx)}
                      disabled={state === "locked"}
                      className="flex flex-col items-center gap-1.5 group"
                    >
                      <div
                        className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm font-semibold transition-all duration-300 ${stepCircleClass(idx)}`}
                      >
                        {state === "done" ? (
                          <Check size={14} strokeWidth={2.5} />
                        ) : state === "locked" ? (
                          <Lock size={12} />
                        ) : (
                          idx + 1
                        )}
                      </div>
                      <span
                        className={`text-[11px] whitespace-nowrap transition-colors duration-300 ${stepLabelClass(idx)}`}
                      >
                        {step.label}
                      </span>
                    </button>
                    {idx < STEPS.length - 1 && (
                      <div
                        className={`flex-1 h-[2px] mx-3 mb-5 rounded-full transition-all duration-500 ${connectorClass(idx)}`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <AnimatePresence mode="wait">
              {/* ── Step 0: Location Search ── */}
              {currentStep === 0 && (
                <motion.div
                  key="step0"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-4"
                >
                  <div className="relative h-[400px] w-full rounded-2xl overflow-hidden border border-gray-200 shadow-xl bg-gray-50">
                    {!mapError ? (
                      <MapErrorBoundary onError={handleMapError}>
                        <Map
                          mapId="DEMO_MAP_ID"
                          defaultCenter={{
                            lat: Number(formData.latitude) || -1.2921,
                            lng: Number(formData.longitude) || 36.8219,
                          }}
                          defaultZoom={17}
                          onClick={handleMapClick}
                          disableDefaultUI={true}
                          zoomControl={true}
                          zoomControlOptions={{
                            position: ControlPosition.RIGHT_CENTER,
                          }}
                          styles={[{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }]}
                          style={{ width: "100%", height: "100%" }}
                        >
                          <MapController target={panTarget} />
                          {placePicked && (
                            <AdvancedMarker position={{ lat: formData.latitude, lng: formData.longitude }}>
                              <MapMarkerPin />
                            </AdvancedMarker>
                          )}
                        </Map>
                      </MapErrorBoundary>
                    ) : (
                      <div className="absolute inset-0 bg-gray-50 flex items-center justify-center p-6 text-center">
                        <div className="max-w-xs animate-in fade-in zoom-in duration-500">
                          <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="w-6 h-6 text-amber-600" />
                          </div>
                          <h3 className="text-base font-bold text-gray-900 mb-1">Map Loading Failed</h3>
                          <p className="text-sm text-gray-500 mb-4">
                            We couldn't initialize Google Maps. You can still enter the address manually.
                          </p>
                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={() => setCurrentStep(1)}
                              className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all"
                            >
                              Enter address manually
                            </button>
                            <button
                              type="button"
                              onClick={() => setMapError(false)}
                              className="text-xs font-medium text-gray-500 hover:text-blue-600 underline"
                            >
                              Try reloading map
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Absolute positioned search - Top center */}
                    {!mapError && (
                      <div className="absolute top-4 inset-x-4 z-20">
                        <div className="max-w-md mx-auto">
                          <GooglePlacesSearchInput
                            onPlaceSelected={handlePlaceSelected}
                            placeholder="Search for your building..."
                            showSelectedBadge={false}
                            className="shadow-2xl"
                          />
                        </div>
                      </div>
                    )}

                    {!mapError && !placePicked && (
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none whitespace-nowrap">
                        <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg border border-gray-100 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Click on the map to pin your building
                          </span>
                        </div>
                      </div>
                    )}

                    {placePicked && !mapError && (
                      <div className="absolute bottom-4 left-4 right-16 sm:right-auto sm:max-w-md z-20">
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-xl shadow-2xl p-4 border border-gray-100 flex items-center gap-3"
                        >
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">
                              {formData.buildingName || "Location Selected"}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{formData.streetAddress}</p>
                          </div>
                        </motion.div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ── Step 1: Address Details ── */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">Building Name</label>
                      <Input
                        value={formData.buildingName}
                        onChange={e => handleInputChange("buildingName", e.target.value)}
                        placeholder="Downtown Business Center"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">Street Address</label>
                      <Input
                        value={formData.streetAddress}
                        onChange={e => handleInputChange("streetAddress", e.target.value)}
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        City <span className="text-red-400">*</span>
                      </label>
                      <Input
                        value={formData.city}
                        onChange={e => handleInputChange("city", e.target.value)}
                        placeholder="Nairobi"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Region / State <span className="text-red-400">*</span>
                      </label>
                      <Input
                        value={formData.region}
                        onChange={e => handleInputChange("region", e.target.value)}
                        placeholder="Nairobi County"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">Country</label>
                      <Input
                        value={formData.country}
                        onChange={e => handleInputChange("country", e.target.value)}
                        placeholder="Kenya"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Postal Code <span className="text-red-400">*</span>
                      </label>
                      <Input
                        value={formData.postalCode}
                        onChange={e => handleInputChange("postalCode", e.target.value)}
                        placeholder="00100"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <TimezoneSelect
                        value={formData.timezone}
                        onChange={value => handleInputChange("timezone", value)}
                        label="Building Timezone"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block mb-1 text-sm font-medium text-gray-700">Digital Address</label>
                      <Input
                        value={formData.digitalAddress}
                        onChange={e => handleInputChange("digitalAddress", e.target.value)}
                        placeholder="Plus code or GPS address (optional)"
                      />
                    </div>
                  </div>

                  {/* Images */}
                  <div className="pt-4 border-t border-gray-100">
                    <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                      <Image className="w-4 h-4 text-gray-400" />
                      Building Images
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={e => handleImageUpload(e.target.files)}
                      className="w-full text-sm p-2 rounded-lg border border-gray-300 cursor-pointer file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      disabled={imageUploading}
                    />
                    {imageUploading && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-blue-600">
                        <div className="w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        Uploading…
                      </div>
                    )}
                    {formData.images.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                        {formData.images.map((url, i) => (
                          <div key={i} className="relative group">
                            <img
                              src={url}
                              alt={`Building ${i + 1}`}
                              className="w-full h-20 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-75 group-hover:opacity-100 hover:bg-red-600 transition-all duration-200 shadow-sm"
                              title="Remove image"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ── Step 2: Floors ── */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  {floorsUnlocked ? (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-500">
                        Add, rename or remove floors for{" "}
                        <span className="font-medium text-gray-700">
                          {editingAddress?.buildingName || "this building"}
                        </span>
                        .
                      </p>
                      <ManageFloorsDialog
                        buildingAddressId={editingAddress.id}
                        triggerButton={
                          <button
                            type="button"
                            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 hover:border-indigo-300 transition-all"
                          >
                            <Layers className="w-4 h-4" />
                            Open Floor Manager
                          </button>
                        }
                        onUpdated={() => fetchBuildingAddresses(0)}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <Lock className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">Save the address first</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Go back to step 2 and save to unlock floor management.
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Navigation */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            {/* Left: Cancel / Back */}
            <div>
              {currentStep === 0 ? (
                <button
                  type="button"
                  onClick={closeDialog}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setCurrentStep(s => s - 1)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
                >
                  ← Back
                </button>
              )}
            </div>

            {/* Right: action buttons */}
            <div className="flex items-center gap-2">
              {/* Step 0 → Next (Only if place picked or map error) */}
              {currentStep === 0 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  disabled={!placePicked && !editingAddress && !mapError}
                  className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Next <ChevronRight size={15} />
                </button>
              )}

              {/* Step 1 → Save (create or update) */}
              {currentStep === 1 && (
                <button
                  type="button"
                  onClick={editingAddress ? handleUpdateAddress : handleCreateAddress}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {isSaving ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving…
                    </>
                  ) : editingAddress ? (
                    <>
                      Save Details <ChevronRight size={15} />
                    </>
                  ) : (
                    <>
                      Create & Continue <ChevronRight size={15} />
                    </>
                  )}
                </button>
              )}

              {/* Step 2 → Done */}
              {currentStep === 2 && (
                <button
                  type="button"
                  onClick={closeDialog}
                  className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all"
                >
                  <Check size={14} /> Done
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Building Addresses List */}
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin" />
        </div>
      ) : buildingAddresses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
            <Building className="w-8 h-8 text-blue-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900">No building addresses yet</h3>
          <p className="mt-1.5 text-sm text-gray-500 max-w-xs leading-relaxed">
            Add your first building address to start organising your spaces by location.
          </p>
          <button
            onClick={handleOpenCreateForm}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <Plus size={15} /> Add Building Address
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {buildingAddresses.map(address => {
            const hasImage = address.images?.length > 0;
            const floorCount = Array.isArray(address.floors) ? address.floors.length : 0;
            const floorLabels = Array.isArray(address.floors)
              ? address.floors.map(f => f.label || `Floor ${f.floorNumber}`).slice(0, 3)
              : [];
            const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${address.latitude},${address.longitude}`;
            // Pick a deterministic gradient based on the address id
            const gradients = [
              "from-blue-500 to-indigo-600",
              "from-violet-500 to-purple-600",
              "from-teal-500 to-cyan-600",
              "from-rose-500 to-pink-600",
              "from-amber-500 to-orange-500",
              "from-emerald-500 to-green-600",
            ];
            const gradient = gradients[(address.id || 0) % gradients.length];

            // Check if user can edit this address
            const isEditable = canEditAddress(address);

            return (
              <div
                key={address.id}
                className={`group flex flex-col rounded-2xl border border-gray-200/80 bg-white overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ${!isEditable ? "opacity-90" : ""}`}
              >
                {/* ── Cover image / placeholder ── */}
                <div className="relative h-36 flex-shrink-0 overflow-hidden">
                  {hasImage ? (
                    <img
                      src={address.images[0]}
                      alt={address.buildingName}
                      className="w-full h-full object-cover"
                      onError={e => {
                        // replace broken img with the gradient placeholder
                        const parent = e.currentTarget.parentNode;
                        e.currentTarget.remove();
                        const fallback = parent.querySelector("[data-fallback]");
                        if (fallback) fallback.classList.remove("hidden");
                      }}
                    />
                  ) : null}
                  {/* No-photo placeholder — shown when no images OR image fails */}
                  <div
                    data-fallback
                    className={`${hasImage ? "hidden" : ""} absolute inset-0 bg-gradient-to-br ${gradient} flex flex-col items-center justify-center gap-2`}
                  >
                    {/* subtle grid pattern overlay */}
                    <div
                      className="absolute inset-0 opacity-10"
                      style={{
                        backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
                        backgroundSize: "18px 18px",
                      }}
                    />
                    <div className="relative flex flex-col items-center gap-1.5">
                      <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Building className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-white/80 text-[11px] font-medium tracking-wide">No photos yet</span>
                    </div>
                  </div>

                  {/* Floating badges on cover */}
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    {floorCount > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-white/90 backdrop-blur-sm text-indigo-700 shadow-sm">
                        <Layers className="w-3 h-3" />
                        {floorCount} {floorCount === 1 ? "floor" : "floors"}
                      </span>
                    )}
                    {hasImage && address.images.length > 1 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-white/90 backdrop-blur-sm text-gray-700 shadow-sm">
                        <Image className="w-3 h-3" />
                        {address.images.length}
                      </span>
                    )}
                  </div>

                  {/* Top-right: edit + delete OR lock indicator */}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    {isEditable ? (
                      <>
                        <button
                          onClick={() => startEdit(address)}
                          className="p-1.5 bg-white/90 backdrop-blur-sm text-gray-600 rounded-lg hover:text-blue-600 hover:bg-white transition-all shadow-sm"
                          title="Edit building"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          onClick={() => handleDeleteAddress(address)}
                          className="p-1.5 bg-white/90 backdrop-blur-sm text-gray-600 rounded-lg hover:text-red-500 hover:bg-white transition-all shadow-sm"
                          title="Delete building"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    ) : (
                      <div
                        className="flex items-center gap-1 px-2 py-1 bg-amber-50/95 backdrop-blur-sm rounded-lg shadow-sm"
                        title="This address belongs to another organization and cannot be edited"
                      >
                        <Lock size={12} className="text-amber-600" />
                        <span className="text-[10px] font-medium text-amber-700">Read-only</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Card body ── */}
                <div className="flex flex-col flex-1 px-4 pt-3.5 pb-4 gap-3">
                  {/* Name + country */}
                  <div>
                    <h4 className="text-[15px] font-semibold text-gray-900 leading-tight truncate">
                      {address.buildingName}
                    </h4>
                    <p className="text-xs text-gray-400 mt-0.5 font-medium tracking-wide uppercase">
                      {[address.city, address.country].filter(Boolean).join(" · ")}
                    </p>
                  </div>

                  {/* Address detail */}
                  <div className="flex items-start gap-2 text-sm text-gray-500">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-300" />
                    <span className="leading-snug">
                      {[address.streetAddress, address.region, address.postalCode].filter(Boolean).join(", ")}
                    </span>
                  </div>

                  {/* Floor pills */}
                  {floorCount > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {floorLabels.map((label, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 text-[11px] font-medium rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100"
                        >
                          {label}
                        </span>
                      ))}
                      {floorCount > 3 && (
                        <span className="px-2 py-0.5 text-[11px] font-medium rounded-md bg-gray-100 text-gray-500 border border-gray-200">
                          +{floorCount - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Meta — digital address */}
                  {address.digitalAddress && (
                    <div className="flex flex-wrap gap-2 pt-0.5">
                      <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                        <Hash className="w-3 h-3" /> {address.digitalAddress}
                      </span>
                    </div>
                  )}

                  {/* ── Action row ── */}
                  <div className="flex items-center gap-2 pt-1 mt-auto border-t border-gray-100">
                    {/* Google Maps */}
                    <a
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View on Maps
                    </a>

                    {/* Manage Floors - available for ALL addresses regardless of ownership */}
                    {canManageFloors(address) && (
                      <ManageFloorsDialog
                        buildingAddressId={address.id}
                        triggerButton={
                          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors">
                            <Layers className="w-3.5 h-3.5" />
                            Manage Floors
                          </button>
                        }
                        onUpdated={() => fetchBuildingAddresses(0)}
                      />
                    )}

                    {/* Edit/Delete — only for editable addresses (created by user's org) */}
                    {isEditable && (
                      <>
                        <button
                          onClick={() => startEdit(address)}
                          className="ml-auto p-1.5 text-gray-400 rounded-lg hover:text-blue-600 hover:bg-blue-50 transition-all sm:opacity-0 sm:group-hover:opacity-100"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteAddress(address)}
                          className="p-1.5 text-gray-400 rounded-lg hover:text-red-500 hover:bg-red-50 transition-all sm:opacity-0 sm:group-hover:opacity-100"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}

                    {/* Read-only indicator for non-editable addresses (created by other orgs) */}
                    {!isEditable && (
                      <div className="ml-auto flex items-center gap-1.5 px-2 py-1 text-amber-600">
                        <Lock size={12} />
                        <span className="text-[10px] font-medium">Read-only</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-8 border-t border-gray-100 pt-6">
          <div className="text-sm text-gray-500">
            Showing <span className="font-medium">{currentPage * pageSize + 1}</span> to{" "}
            <span className="font-medium">{Math.min((currentPage + 1) * pageSize, totalElements)}</span> of{" "}
            <span className="font-medium">{totalElements}</span> results
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              Previous
            </Button>
            <div className="flex gap-1">
              {[...Array(totalPages)].map((_, i) => (
                <Button
                  key={i}
                  variant={currentPage === i ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(i)}
                  className="w-8 h-8 p-0"
                >
                  {i + 1}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage === totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Building Address</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this building address? This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setAddressToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={confirmDeleteAddress} className="bg-[#3A36DB] hover:bg-[#2d2bb8] text-white">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
};

export default BuildingAddressSettings;
